import { format } from 'date-fns'

import type { ApprovalInstanceDto } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import type {
  ProgressHeaderStatus,
  ProgressLayer,
  ProgressBaseEvent,
  ProgressEvent,
  ProgressBadge
} from '@/modules/company/Approvals/AuditoriumExceptionalities/types/progress-timeline.types'

export interface ApprovalTimelineInput {
  approvalInstance: ApprovalInstanceDto
  requestDate: string | Date
  requesterName: string
}

export interface ApprovalTimelineProps {
  headerStatus: ProgressHeaderStatus
  title: string
  layers: ProgressLayer[]
  baseEvent: ProgressBaseEvent
}

const ROLE_EVENT_LABELS: Record<string, string> = {
  OPERATION: 'Operacional',
  MEDICAL_COORDINATOR: 'Tático',
  AUDIT_ANALYST: 'Análise de Risco',
  TECHNICAL_VALIDATOR: 'Técnica',
  STRATEGIC_VALIDATOR: 'Estratégica',
  EXECUTIVE_VALIDATOR: 'Executiva'
}

const ROLE_BADGE_MAP: Record<string, ProgressBadge> = {
  OPERATION: { text: 'Adm', variant: 'primary' },
  MEDICAL_COORDINATOR: { text: 'Med', variant: 'dark' },
  AUDIT_ANALYST: { text: 'Adm', variant: 'primary' },
  TECHNICAL_VALIDATOR: { text: 'Med', variant: 'dark' },
  STRATEGIC_VALIDATOR: { text: 'Med', variant: 'dark' },
  EXECUTIVE_VALIDATOR: { text: 'Med', variant: 'dark' }
}

function getTimezoneLabel(): string {
  const offsetMinutes = new Date().getTimezoneOffset()
  const sign = offsetMinutes <= 0 ? '+' : '-'
  const absHours = Math.floor(Math.abs(offsetMinutes) / 60)
  return `UTC${sign}${absHours}`
}

function formatTimelineDate(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (isNaN(date.getTime())) return ''
  return format(date, 'dd/MM/yyyy')
}

function formatTimelineTime(dateInput: string | Date, userName?: string | null): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
  if (isNaN(date.getTime())) return ''
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const tz = getTimezoneLabel()
  const timeStr = `${hours}:${minutes} ${tz}`
  return userName ? `${timeStr} por ${userName}` : timeStr
}

function getDecisionDescription(role: string, decision: string): string {
  const roleLabel = ROLE_EVENT_LABELS[role] ?? role
  const statusLabel =
    decision === 'APPROVED' ? 'aprovado' : decision === 'REJECTED' ? 'recusado' : 'pendente'
  return `${roleLabel} ${statusLabel}`
}

function mapDecisionStatus(decision: string): ProgressEvent['status'] {
  if (decision === 'APPROVED') return 'approved'
  if (decision === 'REJECTED') return 'rejected'
  if (decision === 'PENDING') return 'pending'
  return 'neutral'
}

export function mapApprovalToTimeline(input: ApprovalTimelineInput): ApprovalTimelineProps {
  const { approvalInstance, requestDate, requesterName } = input

  const sortedLayers = [...approvalInstance.layers].sort((a, b) => b.order - a.order)

  const decisionsByLayer = new Map<number, typeof approvalInstance.decisions>()
  for (const d of approvalInstance.decisions) {
    if (!decisionsByLayer.has(d.layerOrder)) decisionsByLayer.set(d.layerOrder, [])
    decisionsByLayer.get(d.layerOrder)!.push(d)
  }

  let completedLayersCount = 0
  for (const layer of approvalInstance.layers) {
    const layerDecisions = decisionsByLayer.get(layer.order) ?? []
    const allApproved =
      layerDecisions.length > 0 && layerDecisions.every((d) => d.decision === 'APPROVED')
    if (allApproved) completedLayersCount++
  }

  let headerStatus: ProgressHeaderStatus
  let title: string

  if (approvalInstance.status === 'COMPLETED') {
    headerStatus = { type: 'approved' }
    title = 'Pedido aprovado'
  } else if (approvalInstance.status === 'REJECTED') {
    headerStatus = { type: 'rejected' }
    title = 'Pedido recusado'
  } else {
    headerStatus = {
      type: 'in-progress',
      current: completedLayersCount,
      total: approvalInstance.layers.length
    }
    title = `Progresso ${completedLayersCount}/${approvalInstance.layers.length}`
  }

  const layers: ProgressLayer[] = sortedLayers.map((layer) => {
    const operatorLabel = layer.operator === 'AND' ? 'Todos - E' : 'Qualquer - OU'
    const layerDecisions = decisionsByLayer.get(layer.order) ?? []

    const decisionEvents: ProgressEvent[] = layerDecisions
      .sort((a, b) => {
        if (!a.decidedAt && !b.decidedAt) return 0
        if (!a.decidedAt) return -1
        if (!b.decidedAt) return 1
        return new Date(b.decidedAt).getTime() - new Date(a.decidedAt).getTime()
      })
      .map((d) => ({
        date: d.decidedAt ? formatTimelineDate(d.decidedAt) : '',
        description: getDecisionDescription(d.role, d.decision),
        time: d.decidedAt ? formatTimelineTime(d.decidedAt, d.userName) : '',
        status: mapDecisionStatus(d.decision),
        badge: ROLE_BADGE_MAP[d.role]
      }))

    const allApproved =
      layerDecisions.length > 0 && layerDecisions.every((d) => d.decision === 'APPROVED')

    const events: ProgressEvent[] = []

    if (allApproved && layerDecisions.length >= 2) {
      const latestDecision = layerDecisions.reduce((latest, d) => {
        if (!d.decidedAt) return latest
        if (!latest.decidedAt) return d
        return new Date(d.decidedAt) > new Date(latest.decidedAt) ? d : latest
      })

      events.push({
        date: latestDecision.decidedAt ? formatTimelineDate(latestDecision.decidedAt) : '',
        description: 'Etapa concluída',
        time: latestDecision.decidedAt
          ? formatTimelineTime(latestDecision.decidedAt, latestDecision.userName)
          : '',
        status: 'approved'
      })
    }

    events.push(...decisionEvents)

    return { operatorLabel, events }
  })

  const baseEvent: ProgressBaseEvent = {
    date: formatTimelineDate(requestDate),
    description: 'Pedido criado',
    time: formatTimelineTime(requestDate, requesterName)
  }

  return { headerStatus, title, layers, baseEvent }
}
